'use client';

import { requestLogin } from '@/lib/auth/login-dialog';

import { useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { projectApi } from '@/lib/api/projects';
import { toast } from '@/lib/stores/toast-store';
import { getMembershipLimitErrorCode, handleSubscriptionEntitlementApiError } from '@/lib/subscription-entitlements';
import { getAuthSessionVersion, useAuthStore } from '@/stores/authStore';
import { mapProjectApiRecord, useProjectStore } from '@/stores/projectStore';
import { SystemLoading } from '@/components/ui/SystemLoading';

function WorkspaceLoading() {
  const t = useTranslations('common');
  return <SystemLoading fill="parent" label={t('loading')} />;
}
const ShootingWorkspace = dynamic(() => import('@/components/shooting/ShootingWorkspace').then(module => module.ShootingWorkspace), { ssr: false, loading: WorkspaceLoading });
const RETURN_PATH = '/explore?tool=virtual-try-on';

/** Browse and choose inputs freely; allocate a project only for explicit generation. */
export function useVirtualTryOnProject() {
  const t = useTranslations('shooting');
  const userId = useAuthStore(state => state.user?.id);
  const pendingProject = useRef<{ owner: string; epoch: number; promise: Promise<string> } | null>(null);
  const executionProject = useRef<{ owner: string; id: string } | null>(null);
  const sessionVersion = getAuthSessionVersion();
  const identity = useRef({ owner: userId, sessionVersion, epoch: 0 });
  const mounted = useRef(true);
  if (identity.current.owner !== userId || identity.current.sessionVersion !== sessionVersion) {
    identity.current = { owner: userId, sessionVersion, epoch: identity.current.epoch + 1 };
    pendingProject.current = null;
    executionProject.current = null;
  }
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  return useCallback(async () => {
    const ownerId = useAuthStore.getState().user?.id;
    const requestSession = getAuthSessionVersion();
    if (!ownerId || ownerId !== identity.current.owner) throw new Error('AUTH_SESSION_CHANGED');
    // Logout/login can finish in one React batch with the same user id.
    if (identity.current.sessionVersion !== requestSession) {
      identity.current = { owner: ownerId, sessionVersion: requestSession, epoch: identity.current.epoch + 1 };
      pendingProject.current = null;
      executionProject.current = null;
    }
    const epoch = identity.current.epoch;
    if (executionProject.current?.owner === ownerId) return executionProject.current.id;
    if (pendingProject.current?.owner === ownerId && pendingProject.current.epoch === epoch) return pendingProject.current.promise;
    const create = async () => {
      const result = await projectApi.createProject({ name: t('title') });
      if (!mounted.current || useAuthStore.getState().user?.id !== ownerId || getAuthSessionVersion() !== requestSession || identity.current.epoch !== epoch) throw new Error('AUTH_SESSION_CHANGED');
      if (!result.success || !result.data?.id) {
        if (getMembershipLimitErrorCode(result.error) === 'PROJECT_LIMIT_EXCEEDED') {
          handleSubscriptionEntitlementApiError({ error: result.error });
        }
        throw result.error ?? new Error('project_create_failed');
      }
      useProjectStore.getState().addProject(mapProjectApiRecord(result.data));
      executionProject.current = { owner: ownerId, id: result.data.id };
      return result.data.id;
    };
    const pending = { owner: ownerId, epoch, promise: create() };
    pendingProject.current = pending;
    try { return await pending.promise; }
    finally { if (pendingProject.current === pending) pendingProject.current = null; }
  }, [t]);
}

/** The standalone route and legacy modal share the exact same execution workspace. */
export function VirtualTryOnWorkspaceEntry({ onClose, presentation = 'modal' }: {
  onClose: () => void;
  presentation?: 'modal' | 'embedded';
}) {
  const userId = useAuthStore(state => state.user?.id);
  const t = useTranslations('shooting');
  const authT = useTranslations('auth');
  const ensureProject = useVirtualTryOnProject();
  if (!userId) return <section className="flex h-full flex-col items-center justify-center gap-5 bg-white p-6" data-virtual-try-on-sign-in>
    <h1 className="text-2xl font-semibold text-[#203644]">{t('title')}</h1>
    <button type="button" className="rounded-full bg-[#4ABDE9] px-6 py-3 font-medium text-[#203644] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4ABDE9]"
      onClick={() => requestLogin(`/login?${new URLSearchParams({ redirect: '/tools/virtual-try-on' })}`)}>{authT('login')}</button>
  </section>;
  return <ShootingWorkspace onEnsureProject={ensureProject} onClose={onClose} presentation={presentation} />;
}

/** Open the same reference-outfit workspace used by the canvas toolbar. */
export function ExploreVirtualTryOnButton({ children, disabled, resumeFromUrl = true, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { resumeFromUrl?: boolean }) {
  const t = useTranslations('shooting');
  const userId = useAuthStore(state => state.user?.id);
  const [workspaceOwner, setWorkspaceOwner] = useState<string | null>(null);
  const ensureProject = useVirtualTryOnProject();
  const [opening, setOpening] = useState(false);
  const openingRef = useRef(false);
  const handledReturnOwner = useRef<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const openWorkspace = useCallback(async () => {
    if (openingRef.current) return;
    const ownerId = useAuthStore.getState().user?.id;
    if (!ownerId) {
      requestLogin(`/login?${new URLSearchParams({ redirect: RETURN_PATH })}`);
      return;
    }
    openingRef.current = true;
    setOpening(true);
    try {
      setWorkspaceOwner(ownerId);
      const url = new URL(window.location.href);
      if (url.searchParams.get('tool') === 'virtual-try-on') {
        url.searchParams.delete('tool');
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (error) {
      if (mounted.current && useAuthStore.getState().user?.id === ownerId) {
        toast.showError(error, 'canvas', { fallbackMessage: t('launchFailed') });
      }
    } finally {
      openingRef.current = false;
      if (mounted.current) setOpening(false);
    }
  }, [t]);

  useEffect(() => {
    if (!userId) {
      setWorkspaceOwner(null);
      return;
    }
    // Only the homepage entry resumes the login URL; sidebar buttons open on click.
    if (!resumeFromUrl) return;
    if (handledReturnOwner.current === userId) return;
    if (new URL(window.location.href).searchParams.get('tool') !== 'virtual-try-on') return;
    handledReturnOwner.current = userId;
    void openWorkspace();
  }, [userId, openWorkspace, resumeFromUrl]);

  return <>
    <button {...props} type="button" disabled={disabled || opening} aria-busy={opening} onClick={() => void openWorkspace()}>
      {children}
    </button>
    {workspaceOwner === userId && userId && <ShootingWorkspace onEnsureProject={ensureProject} onClose={() => setWorkspaceOwner(null)} />}
  </>;
}
